/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {StackPullRequestFragment} from './generated/graphql';
import type GitHubClient from './github/GitHubClient';
import type {StackedPullRequest} from './jotai';

import {
  gitHubClientAtom,
  gitHubPullRequestAtom,
  stackedPullRequestAtom,
  stackedPullRequestFragmentsAtom,
} from './jotai';
import {MergeableState, PullRequestMergeMethod, PullRequestReviewDecision, PullRequestState} from './generated/graphql';
import {LANDED_COMMENT_MARKER} from './landedMarker';
import {Box, Button, Dialog, Text, ActionMenu, ActionList} from '@primer/react';
import {useAtomValue} from 'jotai';
import {unwrap} from 'jotai/utils';
import {useCallback, useState} from 'react';

const unwrappedStackAtom = unwrap(stackedPullRequestFragmentsAtom);
const unwrappedClientAtom = unwrap(gitHubClientAtom);
const unwrappedStackTypeAtom = unwrap(stackedPullRequestAtom, prev => prev ?? {type: 'no-stack' as const});

type LandingState =
  | {type: 'idle'}
  | {type: 'confirming'}
  | {type: 'landing'; step: string; progress: number; total: number}
  | {type: 'done'; merged: number; closed: number}
  | {type: 'error'; message: string};

const MERGE_METHOD_LABELS: Record<PullRequestMergeMethod, string> = {
  [PullRequestMergeMethod.Squash]: 'Squash and merge',
  [PullRequestMergeMethod.Merge]: 'Create a merge commit',
  [PullRequestMergeMethod.Rebase]: 'Rebase and merge',
};

/**
 * Lands a stack of PRs. Both sapling and ghstack head branches are cumulative
 * (the top PR's branch contains ALL commits from the stack). Strategy:
 *
 * 1. For ghstack: retarget the top PR to trunk (sapling PRs already target trunk)
 * 2. Rebase-merge the top PR → each commit lands individually on trunk
 * 3. Close lower PRs (their changes are already included in the top PR)
 * 4. For partial landings: retarget the PR above to trunk so the user can rebase
 *
 * This preserves full commit history (rebase replays each commit individually).
 */
async function landStack(
  client: GitHubClient,
  stackType: StackedPullRequest,
  prsToLand: StackPullRequestFragment[],
  prAbove: StackPullRequestFragment | null,
  mergeMethod: PullRequestMergeMethod,
  onProgress: (step: string, progress: number, total: number) => void,
): Promise<{merged: number; closed: number}> {
  const total = prsToLand.length;
  const topPR = prsToLand[0];
  const isGhstack = stackType.type === 'ghstack';
  const isManual = stackType.type === 'manual';
  const trunkBranch = isGhstack
    ? await client.getDefaultBranch()
    : (prsToLand[prsToLand.length - 1]?.baseRefName || 'main');

  // Manual stacks: each PR only contains its own commits and targets its
  // parent branch. Merge bottom-up (each PR into its base).
  if (isManual) {
    let merged = 0;
    for (let i = prsToLand.length - 1; i >= 0; i--) {
      const pr = prsToLand[i];
      if (pr.state !== PullRequestState.Open) {
        continue;
      }
      onProgress(`Merging D${pr.number}...`, prsToLand.length - i, total);
      await client.mergePullRequest({pullRequestId: pr.id, mergeMethod});
      merged++;
    }

    // Clean up branches
    const branchesToDelete = new Set<string>();
    for (const pr of prsToLand) {
      if (pr.headRefName && pr.headRefName !== trunkBranch) {
        branchesToDelete.add(pr.headRefName);
      }
    }
    if (branchesToDelete.size > 0) {
      onProgress('Cleaning up branches...', total, total);
      await Promise.all(
        [...branchesToDelete].map(branch => client.deleteBranch(branch)),
      );
    }

    return {merged, closed: 0};
  }

  // For ghstack, the top PR targets a ghstack base branch, not trunk.
  // Retarget it to trunk before merging.
  if (isGhstack) {
    onProgress(`Retargeting D${topPR.number} to ${trunkBranch}...`, 0, total);
    await client.updatePullRequestBase(topPR.id, trunkBranch);
  }

  // Rebase-merge the top PR. Its head branch contains all cumulative commits,
  // so rebase replays each one individually onto trunk.
  onProgress(`Merging D${topPR.number}...`, 0, total);
  await client.mergePullRequest({pullRequestId: topPR.id, mergeMethod});

  // Close lower PRs — their changes are already on trunk via the top PR.
  let closed = 0;
  for (let i = 1; i < prsToLand.length; i++) {
    const pr = prsToLand[i];
    if (pr.state !== PullRequestState.Open) {
      continue;
    }
    onProgress(`Closing D${pr.number}...`, i, total);
    await client.addComment(pr.id, `${LANDED_COMMENT_MARKER}Landed via #${topPR.number}`);
    await client.closePullRequest(pr.id);
    closed++;
  }

  // For partial landings with ghstack: retarget the PR above the landed
  // portion to trunk. The user must then rebase it (`sl ghstack`) so its
  // head branch only contains commits not yet on trunk.
  if (isGhstack && prAbove != null) {
    onProgress(`Retargeting D${prAbove.number} to ${trunkBranch}...`, total, total);
    await client.updatePullRequestBase(prAbove.id, trunkBranch);
  }

  // Clean up stale branches created by `sl ghstack` or `sl pr submit`.
  // Delete head branches for all landed/closed PRs. For ghstack, also delete
  // base branches (which are ghstack-managed, not trunk).
  const branchesToDelete = new Set<string>();
  for (const pr of prsToLand) {
    if (pr.headRefName && pr.headRefName !== trunkBranch) {
      branchesToDelete.add(pr.headRefName);
    }
    if (isGhstack && pr.baseRefName && pr.baseRefName !== trunkBranch) {
      branchesToDelete.add(pr.baseRefName);
    }
  }
  if (branchesToDelete.size > 0) {
    onProgress('Cleaning up branches...', total, total);
    await Promise.all(
      [...branchesToDelete].map(branch => client.deleteBranch(branch)),
    );
  }

  return {merged: 1, closed};
}

export default function LandStackButton(): React.ReactElement | null {
  const pullRequest = useAtomValue(gitHubPullRequestAtom);
  const stack = useAtomValue(unwrappedStackAtom) ?? null;
  const stackType: StackedPullRequest = useAtomValue(unwrappedStackTypeAtom);
  const client = useAtomValue(unwrappedClientAtom) ?? null;
  const [state, setState] = useState<LandingState>({type: 'idle'});
  const [mergeMethod, setMergeMethod] = useState<PullRequestMergeMethod>(
    PullRequestMergeMethod.Rebase,
  );

  // Build the list of PRs to land. For stacks, include the current PR and
  // everything below it (landing "up to" the current PR). The stack is
  // top-first, so we find the current PR's index and slice from there.
  const isOpen = pullRequest != null && pullRequest.state === PullRequestState.Open;
  const isStack = stack != null && stack.length > 1;
  const currentIdx = isOpen
    ? (stack?.findIndex(pr => pr.number === pullRequest.number) ?? -1)
    : -1;
  const prsToLand: StackPullRequestFragment[] | null = !isOpen
    ? null
    : isStack && currentIdx >= 0
      ? stack!.slice(currentIdx).filter(pr => pr.state === PullRequestState.Open)
      : [{
          id: pullRequest.id,
          number: pullRequest.number,
          title: pullRequest.title ?? '',
          state: pullRequest.state,
          isDraft: pullRequest.isDraft,
          reviewDecision: pullRequest.reviewDecision ?? null,
          baseRefName: pullRequest.baseRefName ?? 'main',
          headRefName: '',
          headRefOid: '',
          mergeable: MergeableState.Mergeable,
          updatedAt: '',
          comments: {__typename: 'IssueCommentConnection' as const, totalCount: 0},
          lastComment: {__typename: 'IssueCommentConnection' as const, nodes: []},
          reviewThreads: {__typename: 'PullRequestReviewThreadConnection' as const, totalCount: 0},
        }];

  const handleLand = useCallback(async () => {
    if (client == null) {
      setState({type: 'error', message: 'GitHub client not available'});
      return;
    }
    if (prsToLand == null) {
      return;
    }

    try {
      const prAbove = isStack && currentIdx > 0 ? stack![currentIdx - 1] : null;
      const merged = await landStack(
        client,
        stackType,
        prsToLand,
        prAbove,
        mergeMethod,
        (step, progress, total) => setState({type: 'landing', step, progress, total}),
      );
      setState({type: 'done', ...merged});
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setState({type: 'error', message});
    }
  }, [client, stackType, prsToLand, mergeMethod, isStack, currentIdx, stack]);

  const resetState = useCallback(() => setState({type: 'idle'}), []);

  // Must have an open PR to land
  if (prsToLand == null) {
    return null;
  }

  // Only show Land button if all PRs to land are approved
  const allApproved = prsToLand.every(
    pr => pr.reviewDecision === PullRequestReviewDecision.Approved,
  );
  if (!allApproved) {
    return null;
  }

  // Check if any PR in the stack has merge conflicts
  const conflictingPR = prsToLand.find(
    pr => pr.mergeable === MergeableState.Conflicting,
  );
  if (conflictingPR != null) {
    return (
      <Box display="flex" alignItems="center">
        <Text color="danger.fg" fontSize={1}>
          D{conflictingPR.number} has merge conflicts. Rebase required before landing.
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" alignItems="center">
        <LandButtonGroup
          label={isStack ? 'Land Stack' : 'Land'}
          mergeMethod={mergeMethod}
          onMergeMethodChange={setMergeMethod}
          onLand={() => setState({type: 'confirming'})}
          disabled={state.type === 'landing'}
        />
      </Box>

      {state.type === 'confirming' && (
        <ConfirmDialog
          prsToLand={prsToLand}
          mergeMethod={mergeMethod}
          isGhstack={stackType.type === 'ghstack'}
          onConfirm={handleLand}
          onCancel={resetState}
        />
      )}

      {state.type === 'landing' && (
        <ProgressDialog state={state} />
      )}

      {state.type === 'done' && (
        <DoneDialog merged={state.merged} closed={state.closed} onClose={resetState} />
      )}

      {state.type === 'error' && (
        <ErrorDialog message={state.message} onClose={resetState} />
      )}
    </>
  );
}

function LandButtonGroup({
  label,
  mergeMethod,
  onMergeMethodChange,
  onLand,
  disabled,
}: {
  label: string;
  mergeMethod: PullRequestMergeMethod;
  onMergeMethodChange: (method: PullRequestMergeMethod) => void;
  onLand: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <Box display="flex" sx={{gap: 0}}>
      <Button
        variant="primary"
        onClick={onLand}
        disabled={disabled}
        sx={{
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          bg: '#2ea44f',
          borderColor: '#2c974b',
          ':hover:not([disabled])': {bg: '#2c974b'},
        }}>
        {label}
      </Button>
      <ActionMenu>
        <ActionMenu.Anchor>
          <Button
            variant="primary"
            disabled={disabled}
            sx={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: '1px solid rgba(255,255,255,0.2)',
              bg: '#2ea44f',
              borderColor: '#2c974b',
              px: 2,
              ':hover:not([disabled])': {bg: '#2c974b'},
            }}
            aria-label="Select merge method">
            ▾
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList selectionVariant="single">
            {Object.values(PullRequestMergeMethod).map(method => (
              <ActionList.Item
                key={method}
                selected={mergeMethod === method}
                onSelect={() => onMergeMethodChange(method)}>
                {MERGE_METHOD_LABELS[method]}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </Box>
  );
}

function PrListItem({
  pr,
  idx,
  action,
  variant,
}: {
  pr: StackPullRequestFragment;
  idx: number;
  action: string;
  variant: 'success' | 'attention';
}): React.ReactElement {
  return (
    <Box
      as="li"
      sx={{
        py: 1,
        px: 2,
        bg: `${variant}.subtle`,
        borderRadius: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
      <Text color="fg.muted" fontFamily="mono" fontSize={12} sx={{minWidth: 16}}>
        {idx}.
      </Text>
      <Text color={`${variant}.fg`} fontWeight="bold" fontFamily="mono" fontSize={12}>
        {action}
      </Text>
      <Text>
        D{pr.number}: {pr.title}
      </Text>
    </Box>
  );
}

function ConfirmDialog({
  prsToLand,
  mergeMethod,
  isGhstack,
  onConfirm,
  onCancel,
}: {
  prsToLand: StackPullRequestFragment[];
  mergeMethod: PullRequestMergeMethod;
  isGhstack: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const isStack = prsToLand.length > 1;
  return (
    <Dialog isOpen onDismiss={onCancel} aria-labelledby="land-stack-title">
      <Dialog.Header id="land-stack-title">
        {isStack ? 'Land Stack' : 'Land Pull Request'}
      </Dialog.Header>
      <Box p={3}>
        <Text as="p" mb={2}>
          {isStack ? (
            isGhstack ? (
              <>
                This will cascade-merge all {prsToLand.length} PRs and land them on the
                default branch. Each PR will show as merged in GitHub.
              </>
            ) : (
              <>
                This will {MERGE_METHOD_LABELS[mergeMethod].toLowerCase()}{' '}
                <Text fontWeight="bold">D{prsToLand[0].number}</Text> (top of stack, containing all
                commits) and close the {prsToLand.length - 1} PR{prsToLand.length - 1 !== 1 ? 's' : ''} below it.
              </>
            )
          ) : (
            <>
              This will {MERGE_METHOD_LABELS[mergeMethod].toLowerCase()}{' '}
              <Text fontWeight="bold">D{prsToLand[0].number}</Text>.
            </>
          )}
        </Text>
        {isStack && (
          <Box
            as="ul"
            sx={{
              listStyle: 'none',
              pl: 0,
              mb: 3,
            }}>
            {isGhstack ? (
              prsToLand.map((pr, idx) => (
                <PrListItem key={pr.number} pr={pr} idx={idx + 1} action="MERGE" variant="success" />
              ))
            ) : (
              <>
                <PrListItem pr={prsToLand[0]} idx={1} action="MERGE" variant="success" />
                {prsToLand.slice(1).map((pr, idx) => (
                  <PrListItem key={pr.number} pr={pr} idx={idx + 2} action="CLOSE" variant="attention" />
                ))}
              </>
            )}
          </Box>
        )}
        <Box display="flex" justifyContent="flex-end" sx={{gap: 2}}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm} sx={{bg: '#2ea44f', ':hover': {bg: '#2c974b'}}}>
            Confirm Land
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

function ProgressDialog({
  state,
}: {
  state: {step: string; progress: number; total: number};
}): React.ReactElement {
  return (
    <Dialog isOpen aria-labelledby="land-progress-title">
      <Dialog.Header id="land-progress-title">Landing...</Dialog.Header>
      <Box p={3}>
        <Text as="p" mb={2}>{state.step}</Text>
        <Box
          sx={{
            height: 8,
            bg: 'neutral.muted',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              height: '100%',
              width: `${((state.progress + 1) / state.total) * 100}%`,
              bg: '#2ea44f',
              transition: 'width 0.3s ease',
              borderRadius: 2,
            }}
          />
        </Box>
        <Text as="p" fontSize={12} color="fg.muted" mt={1}>
          {state.progress + 1} of {state.total}
        </Text>
      </Box>
    </Dialog>
  );
}

function DoneDialog({
  merged,
  closed,
  onClose,
}: {
  merged: number;
  closed: number;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Dialog isOpen onDismiss={onClose} aria-labelledby="land-done-title">
      <Dialog.Header id="land-done-title">Landed</Dialog.Header>
      <Box p={3}>
        <Text as="p" mb={3}>
          Successfully merged {merged} PR{merged !== 1 ? 's' : ''}
          {closed > 0 && <> and closed {closed} PR{closed !== 1 ? 's' : ''}</>}.
        </Text>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

function ErrorDialog({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Dialog isOpen onDismiss={onClose} aria-labelledby="land-error-title">
      <Dialog.Header id="land-error-title">Landing Failed</Dialog.Header>
      <Box p={3}>
        <Text as="p" mb={3} color="danger.fg">
          {message}
        </Text>
        <Box display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
        </Box>
      </Box>
    </Dialog>
  );
}
